/**
 * Android-only layout helpers for stacked-hero `MainScreen.tsx`.
 * iOS behavior stays in `MainScreen.tsx` + shared `theme/studio/onboardingMotion.ts`.
 */
import { Platform, StyleSheet, type ViewStyle } from 'react-native';
import { androidScaleSize, androidHeaderTopInset } from '../theme/androidUiParity';
import { MAIN_SCREEN_AUTH_BAR, MAIN_SCREEN_DOCK, MAIN_SCREEN_KEYBOARD } from './onboardingMainScreenConfig';
import { appTheme } from '../theme/appTheme';

/** Keep in sync with `MAIN_SCREEN_DOCK.floatZ` in `onboardingMainScreenConfig.ts`. */
export const ANDROID_MAIN_AUTH_FLOAT_Z = MAIN_SCREEN_DOCK.floatZ;

/** Auth chips above dismiss overlay so chevron ↔ label swap stays tappable. */
export const ANDROID_MAIN_AUTH_BAR_Z_WHEN_DOCK = ANDROID_MAIN_AUTH_FLOAT_Z + 10;

/** Matches `welcomeHero` `bottomAuthBtn.minHeight` on Android. */
export function androidMainScreenAuthChipRowHeight(): number {
  return androidScaleSize(40);
}

/** Bottom margin on the auth chip row (`insets.bottom + config gutter`). */
export function androidMainScreenAuthBarOuterMarginBottom(insets_bottom: number): number {
  return insets_bottom + MAIN_SCREEN_AUTH_BAR.marginBottom;
}

/**
 * Distance from the screen bottom to the top of the auth chip row.
 * Used for dismiss tap reserve and Android dock panel placement (chips paint above the float).
 */
export function androidMainScreenAuthChromeBottomInset(insets_bottom: number): number {
  const chip_row_h = Math.max(androidMainScreenAuthChipRowHeight(), MAIN_SCREEN_AUTH_BAR.chipRowHeight);
  return Math.round(androidMainScreenAuthBarOuterMarginBottom(insets_bottom) + chip_row_h);
}

/** Dock idle anchor — `wh * columnBottomInsetFraction` (~16%), aligns panel bottom to hero column bottom (iOS baseline). */
export function mainScreenDockColumnIdleInset(window_height: number): number {
  return window_height * MAIN_SCREEN_DOCK.columnBottomInsetFraction;
}

/**
 * Android: panel bottom must clear the elevated auth chip row.
 * Chips stay above the dock float (z/elevation) so any overlap paints chips on top of the white card.
 */
export function androidMainScreenDockColumnIdleInset(window_height: number, insets_bottom: number): number {
  const ios_baseline = mainScreenDockColumnIdleInset(window_height);
  const chip_clearance = androidMainScreenAuthChromeBottomInset(insets_bottom) + appTheme.spacing.sm;
  return Math.max(ios_baseline, chip_clearance);
}

/** Reserve space so the full-screen dismiss target does not cover the auth chip row. */
export function androidMainScreenAuthBarDismissReserveBottom(insets_bottom: number): number {
  return Math.round(
    androidMainScreenAuthChromeBottomInset(insets_bottom) + MAIN_SCREEN_AUTH_BAR.dismissReserveExtra,
  );
}

/**
 * Hero column shrink-wraps to the fixed-height card on Android (iOS keeps `flex: 1`).
 * Without this, `flex: 1` pins the auth bar to the screen bottom and leaves a large gap under the hero.
 */
export function androidMainScreenHeroColumnLayoutStyle(): ViewStyle {
  return {
    flex: 0,
    flexGrow: 0,
    flexShrink: 0,
  };
}

/** Stable stacking for auth chips — always on, stronger when dock is open. */
export function androidMainScreenAuthBarStyle(dock_active: boolean): ViewStyle | null {
  if (!isMainScreenAndroid()) {
    return null;
  }
  return {
    position: 'relative',
    // zIndex: dock_active ? ANDROID_MAIN_AUTH_BAR_Z_WHEN_DOCK : 46,
    // elevation: dock_active ? 8 : 3,
    zIndex: ANDROID_MAIN_AUTH_BAR_Z_WHEN_DOCK,
    elevation: 8,
    shadowColor: appTheme.colors.black,
    shadowOpacity: 0.5,
    shadowRadius: 1,
    shadowOffset: { width: 1, height: 1 },
  };
}

/** Dismiss tap target above the auth chips (Android z-order / Pressable hit testing). */
export function androidMainScreenDismissPressableStyle(reserved_bottom: number): ViewStyle {
  return {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: reserved_bottom,
  };
}

export function isMainScreenAndroid(): boolean {
  return Platform.OS === 'android';
}

/** Hero above backdrop dim, below auth forms; clears hero shell elevation so forms paint on top (Android). */
export function mainScreenHeroColumnStackStyle(dock_active: boolean): ViewStyle | null {
  if (!dock_active) {
    return null;
  }
  return {
    zIndex: MAIN_SCREEN_DOCK.heroAboveBackdropZ,
    ...(Platform.OS === 'android' ? { elevation: 0 } : {}),
  };
}

/** Dock float layer must outrank hero shell `elevation` on Android. */
export function androidMainScreenDockFloatStyle(dock_active: boolean): ViewStyle | null {
  if (!isMainScreenAndroid() || !dock_active) {
    return null;
  }
  return {
    elevation: 12,
  };
}

export function androidMainScreenDockBackdropLayerStyle(): ViewStyle {
  return {
    ...StyleSheet.absoluteFillObject,
    zIndex: MAIN_SCREEN_DOCK.backdropZ,
    elevation: 0,
  };
}

/**
 * Bottom padding for the floating auth dock column.
 * Idle placement matches iOS: panel bottom sits on the hero column bottom edge (`columnBottomInsetFraction`).
 * Android only overrides when the IME is open.
 */
export function mainScreenDockColumnPaddingBottom(
  window_height: number,
  insets_bottom: number,
  main_dock: 0 | 1 | 2,
  keyboard_height: number,
): number {
  const column_idle = isMainScreenAndroid()
    ? androidMainScreenDockColumnIdleInset(window_height, insets_bottom)
    : mainScreenDockColumnIdleInset(window_height);

  if (!isMainScreenAndroid() || main_dock === 0 || keyboard_height <= 0) {
    return column_idle;
  }

  if (main_dock === 1) {
    return Math.max(
      column_idle,
      keyboard_height - insets_bottom + MAIN_SCREEN_KEYBOARD.panelClearancePx,
    );
  }

  return Math.max(
    column_idle,
    keyboard_height - insets_bottom + MAIN_SCREEN_KEYBOARD.panelClearancePx,
  );
}

/** Android keeps the form mounted through the close animation (`android_dock_form_mounted`). */
export function androidMainScreenDockActive(main_dock: number, form_mounted: boolean): boolean {
  return main_dock > 0 || form_mounted;
}

/**
 * Outer Reanimated shell on Android — transparent so scale-in does not double-paint.
 * Fully opaque `colors.surface` lives on `MainScreenAuthForms` shell + credential scroll (iOS keeps animated fill).
 */
export function androidMainScreenDockPanelStaticStyle(): ViewStyle {
  return {
    backgroundColor: 'transparent',
    transformOrigin: '50% 100% 0px',
    elevation: 0,
  };
}

const HEALTH_PILL_SIZE = androidScaleSize(36);

/**
 * Compact lang pill on the start edge — opposite the health control.
 * Do not `flex: 1` (that stretches the pill across the row on Android).
 */
export const androidMainScreenLangPillStyle: ViewStyle = {
  flex: 0,
  flexGrow: 0,
  flexShrink: 1,
  alignSelf: 'center',
  maxWidth: '58%',
  minHeight: androidScaleSize(32),
};

export const androidMainScreenHealthPillStyle: ViewStyle = {
  width: HEALTH_PILL_SIZE,
  height: HEALTH_PILL_SIZE,
  minWidth: HEALTH_PILL_SIZE,
  maxWidth: HEALTH_PILL_SIZE,
  minHeight: HEALTH_PILL_SIZE,
  maxHeight: HEALTH_PILL_SIZE,
  flex: 0,
  flexShrink: 0,
  alignSelf: 'center',
};

/** Extra top breath under the status bar for lang + health row (hero card). */
export function androidMainScreenChromeTopPadding(insets_top: number): number {
  return androidHeaderTopInset(insets_top) + androidScaleSize(14);
}

/** Same as iOS intrinsic wrapper — explicit maxHeight so ScrollView scrolls inside Reanimated shell on Android. */
export function androidMainScreenIntrinsicFormRootStyle(_max_height?: number): ViewStyle {
  return {
    width: '100%',
    alignSelf: 'stretch',
    flex: 0,
    minHeight: 0,
  };
}

export function androidMainScreenIntrinsicScrollCapStyle(max_height: number): ViewStyle {
  return {
    maxHeight: max_height,
    width: '100%',
    minHeight: 0,
    flexShrink: 1,
    overflow: 'hidden',
  };
}

/**
 * Bottom-sheet main screen: lift the auth panel above the IME (iOS + Android).
 * Pair with `useFormFieldFocusScroll` inside the form — do not also use
 * `ScrollView` `automaticallyAdjustKeyboardInsets` on main-screen intrinsic forms.
 */
export function mainScreenBottomSheetKeyboardPadding(
  form: 'login' | 'register' | null,
  keyboard_height: number,
  insets_bottom: number,
  extra_offset_px = 0,
): number {
  if (!form || keyboard_height <= 0) {
    return 0;
  }
  const base = Math.max(
    0,
    keyboard_height - insets_bottom + MAIN_SCREEN_KEYBOARD.panelClearancePx,
  );
  return base + Math.max(0, extra_offset_px);
}
